import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { FormRow, SpreadsheetContextMenu, SpreadsheetUtils } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import { ncsvCreatorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';

import Worksheet from '../../CSVEditor/Worksheet';
import ColumnsStep from './ColumnsStep';
import { getNameWithoutType } from './CSVService';
import DateStep from './DateStep';
import Header from './Header';
import SymbolsStep from './SymbolsStep';
import ValidateStep from './ValidateStep';

import Papa from 'papaparse';

import './Content.scss';

const EMPTY_FUN = () => {}
const STEPS = ncsvCreatorActions.STEPS
const Content = ({
  activeStep,
  setActiveStep = EMPTY_FUN,
  withHeader = true,
  fileId = undefined,
  rawFile = undefined,
  onClose = EMPTY_FUN,
  noFetching = false,
  withIcon = false,
  ref,
  ...props
}) => {
  const dispatch = useDispatch(),
        [initHeader, setInitHeader] = useState([]),
        [errorCells, setErrorCells] = useState([]),
        [isLoaded, setIsLoaded] = useState(false),
        onLoadEnd = useCallback(() => setIsLoaded(true), []),
        [errorSteps, setErrorSteps] = useState([]),
        [sucessSteps, setSucessSteps] = useState([]),
        [fileInfo, setFileInfo] = useState({}),
        worksheetRef = useRef(),
        getColumns = useCallback(() => worksheetRef.current?.getColumns(), []),
        getMetadataRequest = useRef(),
        formRowRef = useRef(),
        setIsError = useCallback((val, step) => setErrorSteps(steps => val ? steps.addUnique(step) : steps.filter(s => s !== step)), []),
        setIsSucess = useCallback((val, step) => setSucessSteps(steps => val ? steps.addUnique(step) : steps.filter(s => s !== step)), []),
        setIsErrorFirstStep = useCallback(val => setIsError(val, STEPS.DATE), [setIsError]),
        setIsSuccessFirstStep = useCallback(val => setIsSucess(val, STEPS.DATE), [setIsSucess]),
        setIsSuccessSecondStep = useCallback(val => setIsSucess(val, STEPS.SYMBOLS), [setIsSucess]),
        setIsErrorSecondStep = useCallback(val => setIsError(val, STEPS.SYMBOLS), [setIsError]),
        setIsSuccessThirdStep = useCallback(val => setIsSucess(val, STEPS.COLUMNS), [setIsSucess]),
        setIsErrorThirdStep = useCallback(val => setIsError(val, STEPS.COLUMNS), [setIsError]),
        setIsSuccessFourthStep = useCallback(val => setIsSucess(val, STEPS.VALIDATE), [setIsSucess]),
        setIsErrorFourthStep = useCallback(val => setIsError(val, STEPS.VALIDATE), [setIsError]),
        setFileName = useCallback(value => setFileInfo(info => Object.assign({}, info, { fileName: value })), []),
        isError = useCallback(errorCells => {
          let errorColumns = new Set(errorCells.map(cell => cell.columnNumber))
          const header = worksheetRef.current?.getColumns()
                const dateIndex = header.findIndex(c => c?.columnId === "Date")
          errorColumns = Array.from(errorColumns)
          if(errorColumns.some(colIndex => colIndex > dateIndex)) {
            setIsErrorThirdStep(true)
            setIsSuccessThirdStep(false)
          } 
          if(errorColumns.some(colIndex => colIndex === dateIndex)) {
            setIsErrorFirstStep(true)
            setIsSuccessFirstStep(false)
          } 
          if(errorColumns.some(colIndex => colIndex < dateIndex && colIndex !== -1)) {
            setIsErrorSecondStep(true)
            setIsSuccessSecondStep(false)
          }
        }, [setIsErrorFirstStep, setIsErrorSecondStep, setIsErrorThirdStep, setIsSuccessFirstStep, setIsSuccessSecondStep, setIsSuccessThirdStep]),
        getPapaparseConfig = useCallback(reqId => ({
          complete: result => {
            const newResult = result?.data || []
            if(newResult[newResult.length - 1]?.length !== newResult[0]?.length) {
              newResult.pop()
            }
            if(result?.errors.length !== 0) {
              console.error(result?.errors)
              dispatch(globalActions.handleRequest({ id: reqId, noFetching, isResponse: true }))
              onClose()
            } else {
              worksheetRef.current?.setData(newResult)
              worksheetRef.current.setColumns({ columnsArray: result?.meta?.fields.map(c => ({ columnName: c, columnId: c })) || [], data: newResult })
              dispatch(globalActions.handleRequest({ id: reqId, noFetching, isResponse: true }))
            }
          },
          error: () => {
            dispatch(globalActions.handleRequest({ id: reqId, noFetching, isResponse: true }))
            onClose()
          },
          skipEmptyLines: true,
          worker: true,
          header: true
        }), [dispatch, noFetching, onClose]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(async response => {
              const location = response?.headers?.get("Location")
              const parsedData = await response.json()
              return ReduxService.updateObject(parsedData, { location })
            })
            // .withHeader("Target", provider, true)
        , [dispatch]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, enabled: !!fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.location, [infoResponse]),
        downloadFile = useCallback(() => {
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId, noFetching }));
          return Papa.parse(location, Object.assign({ download: true }, getPapaparseConfig(reqId)))
        }, [dispatch, getPapaparseConfig, location, noFetching]),
        dateRef = useRef(),
        symbolsRef = useRef(),
        columnsRef = useRef(),
        onDataChange= useCallback(changes => {
          const columnChanges = changes.filter(c => String(c.rowId).startsWith("header"))
          if(columnChanges.length > 0) {
            setTimeout(() => {
              const columns = worksheetRef.current?.getColumns().map(c => {
                let columnId = c.columnId
                if(c.columnId === columnChanges[0]?.previousCell.text) {
                  columnId = columnChanges[0]?.newCell.text
                }
                return Object.assign({}, c, { columnId })
              })
              const columnNames = columns.map(c => c.columnName)
  
              dateRef.current?.setColumns(columnNames)
              symbolsRef.current?.setColumns(columns)
              columnsRef.current?.setColumnNames(columnNames)
            }, 50)
          } else if(changes[0]?.changeType === SpreadsheetUtils.CHANGE_TYPE.REORDER_COLUMNS) {
            setTimeout(() => { // we need some time as there is a timeout on spreadsheet and async update
              symbolsRef.current?.setColumns(worksheetRef.current?.getColumns())
              columnsRef.current?.setColumnNames(worksheetRef.current?.getColumns().map(c => c.columnName))
            }, 50)
          }
          setErrorCells(errorCells => {
            const columns = getColumns()
            let newErrorCells = errorCells.slice()
            changes.forEach(change => {
              const indexToRemove = newErrorCells.findIndex(i => i?.rowNumber === change?.rowId && columns[i?.columnNumber]?.columnId === change?.columnId)
              newErrorCells = newErrorCells.remove(indexToRemove)
            })
            return newErrorCells
          })
        }, [getColumns]),
        editable = useMemo(() => !(sucessSteps.includes(STEPS.VALIDATE) && activeStep === STEPS.VALIDATE), [activeStep, sucessSteps])

  useEffect(() => () => {
    getMetadataRequest.current?.cancel()
  }, [dispatch])

  useEffect(() => {
    dispatch(ncsvCreatorActions.changeValue({}, 'columnConfigs'))
    if(fileId) {
      downloadFile(fileId)
    } else if(rawFile) {
      const reqId = CommonService.generateUID()
      dispatch(globalActions.handleRequest({ id: reqId }));
      setFileInfo({ fileName: rawFile.name })
      Papa.parse(rawFile, getPapaparseConfig(reqId))
    }
  }, [downloadFile, fileId, rawFile, dispatch, getPapaparseConfig])

  useImperativeHandle(ref, () => ({
    validateForForm: () => formRowRef.current?.validateForForm(),
    isValid: () => formRowRef.current?.isValid(),
    getFileInfo: () => fileInfo,
    getAsCSV: () => worksheetRef.current?.getAsCSV()
  }))

  return (
    <Fragment>
      <FormRow 
          className="ncsv-creator__top"
          ref={formRowRef}
      >
        {withHeader && (
          <Header 
              activeStep={activeStep}
              errorSteps={errorSteps}
              setActiveStep={setActiveStep}
              sucessSteps={sucessSteps}
          />
        )}
        {activeStep === STEPS.DATE && isLoaded && (
          <DateStep 
              ref={dateRef}
              setErrorCells={setErrorCells}
              setInitHeader={setInitHeader}
              setIsError={setIsErrorFirstStep}
              setIsSucess={setIsSuccessFirstStep}
              worksheetRef={worksheetRef}
          />
        )}
        {activeStep === STEPS.SYMBOLS && (
          <SymbolsStep
              initHeader={initHeader}
              ref={symbolsRef}
              setIsError={setIsErrorSecondStep}
              setIsSucess={setIsSuccessSecondStep}
              worksheetRef={worksheetRef}
          />
        )}
        {activeStep === STEPS.COLUMNS && (
          <ColumnsStep
              handler={handler}
              ref={columnsRef}
              setErrorCells={setErrorCells}
              setIsError={setIsErrorThirdStep}
              setIsSucess={setIsSuccessThirdStep}
              worksheetRef={worksheetRef}
          />
        )}
        {activeStep === STEPS.VALIDATE && (
          <ValidateStep
              defaultName={fileInfo?.fileName}
              isError={isError}
              setErrorCells={setErrorCells}
              setIsError={setIsErrorFourthStep}
              setIsSucess={setIsSuccessFourthStep}
              setName={setFileName}
              withIcon={withIcon}
              worksheetRef={worksheetRef}
          />
        )}
      </FormRow>
      <Worksheet
          className="ncsv-creator__bottom"
          editable={editable}
          errorCells={errorCells}
          getHeaderContextMenu={getHeaderContextMenu}
          onDataChange={onDataChange}
          onLoadEnd={onLoadEnd}
          ref={worksheetRef}
          setErrorCells={setErrorCells}
          {...props}
      />
    </Fragment>
  )
}

Content.displayName = "Content"


Content.propTypes = {
  activeStep: PropTypes.string.isRequired,
  fileId: PropTypes.string, 
  noFetching: PropTypes.bool,
  onClose: PropTypes.func,
  rawFile: PropTypes.string, 
  setActiveStep: PropTypes.func,
  withHeader: PropTypes.bool,
  withIcon: PropTypes.bool
}

export default Content;

const TYPES = SpreadsheetContextMenu.TYPES

const getNewCellValue = (value, columnType) => {
  const newText = getNameWithoutType(value)
  return `${newText}(${columnType})`
}

const handler = ({ handleChanges, columnType, cells = undefined, rows, columns, columnId }) => {
  const row = rows[0]
  const localColumnId = cells ? SpreadsheetContextMenu.getColumnId(cells) : columnId
  const colIndex = SpreadsheetContextMenu.getColumnIndex(columns, localColumnId)
  const cellText = row.cells[colIndex]?.text
  const newCellValue = getNewCellValue(cellText, columnType) 
  if(row.cells[colIndex]?.text !== newCellValue) {
    handleChanges([SpreadsheetUtils.getRowChange({ row, colIndex, columnId: localColumnId, newValue: newCellValue })])
  }
}

const getHeaderContextMenu = ({ ...props }) => ([{
  id: "TYPE",
  label: "Type",
  type: TYPES.SUB_MENU,
  editableOnly: true,
  items: [
    {
      id: "valueDate",
      label: "Value Date",
      handler: () => handler({ columnType: "D", ...props })
    },
    {
      id: "valueDouble",
      label: "Value Double",
      handler: () => handler({ columnType: "DB", ...props })
    },
    {
      id: "valueInteger",
      label: "Value Integer",
      handler: () => handler({ columnType: "I", ...props })
    },
    {
      id: "valueString",
      label: "Value String",
      handler: () => handler({ columnType: "S", ...props })
    },
    {
      id: "metadataDate",
      label: "Metadata Date",
      handler: () => handler({ columnType: "MD-D", ...props })
    },
    {
      id: "metadataDouble",
      label: "Metadata Double",
      handler: () => handler({ columnType: "MD-DB", ...props })
    },
    {
      id: "metadataInteger",
      label: "Metadata Integer",
      handler: () => handler({ columnType: "MD-I", ...props })
    },
    {
      id: "metadataString",
      label: "Metadata String",
      handler: () => handler({ columnType: "MD-S", ...props })
    }
  ]
}])
